import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Newspaper, MessagesSquare, Heart } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import Seo from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigestPage() {
  const { tr } = useI18n();
  const { user } = useAuth();
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!user) return;
    axios.get(`${API}/digest`, { headers: { Authorization: `Bearer ${localStorage.getItem("sp_token")}` } })
      .then((r) => setDigest(r.data))
      .catch(() => setDigest({ entries: [] }));
  }, [user]);

  return (
    <div data-testid="digest-page" className="pb-24 max-w-5xl mx-auto px-4 lg:px-8">
      <Seo title="My World Digest — WorldPulse.pro" description="Your weekly summary of news and conversations from the places you follow." canonical="https://worldpulse.pro/digest" />
      <div className="py-12">
        <span className="wp-eyebrow">{tr("My World")}</span>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0F2557] tracking-tight mt-3">{tr("Weekly Digest")}</h1>
        <p className="text-base text-[#475569] mt-3 max-w-2xl">{tr("The past week across the places you follow — fresh headlines and community conversations, in one glance.")}</p>
      </div>

      {!user && (
        <div className="border border-[#BFD4F2] bg-[#F6FAFF] rounded-3xl p-8 text-center">
          <p className="font-bold text-[#0F2557]">{tr("Sign in and follow places to receive your weekly digest.")}</p>
        </div>
      )}
      {user && !digest && (
        <div className="flex items-center gap-3 text-[#2563EB] py-10 justify-center">
          <Loader2 className="animate-spin" /> <span className="font-bold">{tr("Assembling your digest…")}</span>
        </div>
      )}
      {user && digest?.entries.length === 0 && (
        <div className="border border-[#BFD4F2] bg-[#F6FAFF] rounded-3xl p-8 text-center">
          <Heart className="mx-auto text-[#2563EB]" />
          <p className="font-bold text-[#0F2557] mt-3">{tr("You are not following any places yet.")}</p>
          <Link to="/countries" className="inline-block mt-3 text-sm font-bold text-[#2563EB] hover:underline">{tr("Explore countries and tap Follow")} →</Link>
        </div>
      )}

      <div className="space-y-8">
        {digest?.entries.map((e) => (
          <div key={e.place_name} data-testid="digest-entry" className="border border-[#E5EDF7] rounded-3xl overflow-hidden">
            <Link to={e.url} className="flex items-center justify-between px-6 py-4 bg-[#F6FAFF] hover:bg-[#EAF2FD] transition-colors">
              <span className="font-extrabold text-[#0F2557]">{e.flag} {e.place_name}</span>
              <span className="text-xs font-bold text-[#2563EB]">{tr("Open portal")} →</span>
            </Link>
            <div className="grid md:grid-cols-2 gap-6 p-6">
              <div>
                <div className="flex items-center gap-2 text-xs font-extrabold text-[#DC2626] uppercase tracking-wider"><Newspaper size={13} /> {tr("Headlines")}</div>
                <div className="mt-3 space-y-2">
                  {e.headlines.length ? e.headlines.map((h, i) => (
                    <a key={i} href={h.url} target="_blank" rel="noreferrer" className="block text-sm font-bold text-[#0F2557] hover:text-[#DC2626] leading-snug transition-colors">
                      {h.title} <span className="text-[0.6rem] font-semibold text-[#94A3B8]">· {h.source}</span>
                    </a>
                  )) : <p className="text-sm text-[#64748B]">{tr("No fresh headlines this week.")}</p>}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-extrabold text-[#7C3AED] uppercase tracking-wider"><MessagesSquare size={13} /> {tr("Community")} {e.discussion_count > 0 && `(${e.discussion_count} ${tr("new")})`}</div>
                <div className="mt-3 space-y-2">
                  {e.recent_messages.length ? e.recent_messages.map((m) => (
                    <div key={m.id} className="text-sm text-[#475569]"><span className="font-extrabold text-[#6D28D9]">{m.author}:</span> {m.text.slice(0, 90)}{m.text.length > 90 ? "…" : ""}</div>
                  )) : (
                    <Link to={`${e.url}#community`} className="text-sm text-[#64748B] hover:text-[#7C3AED]">{tr("Quiet week — start the conversation")} →</Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
